/**
 * Fabrics, orders ("assignments") and the overview cards —
 * /planning/:project_id/asoebi/* (ans-backend routes/planning/asoebiKal.ts,
 * kal shape only). Exact money: every amount round-trips as the decimal
 * string the server sent, never Number()'d for storage or comparison; only
 * formatted for display via useMoney().
 *
 * In-memory cache (no localStorage for app data), same pattern as every
 * other planning service: refreshXFromApi() re-fetches and replaces the
 * cache; loadX() reads the cache synchronously for render.
 */
import {
  Fabric, FabricAssignment, FabricAssignmentCreate, FabricAssignmentUpdate,
  PaymentInput, BatchPaymentInput, AsoebiOverview, AsoebiStats, AsoebiFilter, GuestOption, GuestAsoebiProfile,
} from '../types/ui/Asoebi';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch, planningPath } from './PlanningApiClient';
import { sumDecimal, subtractDecimal, compareDecimal, isNonZero } from '../utils/decimal';

let fabricsCache: Fabric[] = [];
let assignmentsCache: FabricAssignment[] = [];
let overviewCache: AsoebiOverview | null = null;
let revenueCache: { total: string | null; currency: string | null } | null = null;

const FABRICS_CHANGE_EVENT = 'ans_asoebi_fabrics_change';
const ASSIGNMENTS_CHANGE_EVENT = 'ans_asoebi_assignments_change';
const dispatchFabricsChange = () => window.dispatchEvent(new Event(FABRICS_CHANGE_EVENT));
const dispatchAssignmentsChange = () => window.dispatchEvent(new Event(ASSIGNMENTS_CHANGE_EVENT));

// ── Fabrics ──

export const loadFabrics = (): Fabric[] => fabricsCache;

export const refreshFabricsFromApi = async (archived = false): Promise<Fabric[]> => {
  const res = await apiGet<{ fabrics: Fabric[] }>(planningPath(`/asoebi/fabrics${archived ? '?archived=true' : ''}`));
  if (!archived) {
    fabricsCache = res.fabrics;
    dispatchFabricsChange();
  }
  return res.fabrics;
};

/** Same fetch as refreshFabricsFromApi() but WITHOUT the try/catch a caller may have wrapped it in — callers that need to tell a real failure from an empty catalogue call this directly. */
export const fetchFabricsOrThrow = refreshFabricsFromApi;

export const getFabric = (id: string): Fabric | undefined => fabricsCache.find((f) => f.id === id);

export const createFabric = async (data: Partial<Fabric>): Promise<Fabric> => {
  const created = await apiPost<Fabric>(planningPath('/asoebi/fabrics'), data);
  fabricsCache = [...fabricsCache, created];
  dispatchFabricsChange();
  return created;
};

export const updateFabric = async (id: string, updates: Partial<Fabric>): Promise<Fabric> => {
  const updated = await apiPut<Fabric>(planningPath(`/asoebi/fabrics/${id}`), updates);
  fabricsCache = fabricsCache.map((f) => (f.id === id ? updated : f));
  dispatchFabricsChange();
  return updated;
};

/** Archives the fabric (soft delete); drops it from the active cache. */
export const archiveFabric = async (id: string): Promise<void> => {
  await apiDelete(planningPath(`/asoebi/fabrics/${id}`));
  fabricsCache = fabricsCache.filter((f) => f.id !== id);
  dispatchFabricsChange();
};
export const deleteFabric = archiveFabric;

export const restoreFabric = (id: string): Promise<Fabric> => apiPost<Fabric>(planningPath(`/asoebi/fabrics/${id}/restore`), {});

/** Only an already-archived fabric with no order line pointing at it. */
export const permanentlyDeleteFabric = (id: string): Promise<void> => apiDelete(planningPath(`/asoebi/fabrics/${id}/permanent`));

/**
 * Groups the current assignments cache by guest — a client-side view, not a
 * server resource (the API has no /guests/:id/orders list route yet; only
 * the balance totals at GET asoebi/guests/:guest_id/balance). Archived rows
 * are excluded, matching the dashboard's own "active orders" framing.
 */
export function deriveGuestAsoebiProfiles(assignments: FabricAssignment[]): GuestAsoebiProfile[] {
  const byGuest = new Map<string, FabricAssignment[]>();
  for (const a of assignments) {
    if (a.archived || !a.guest_id) continue;
    byGuest.set(a.guest_id, [...(byGuest.get(a.guest_id) ?? []), a]);
  }
  return [...byGuest.entries()].map(([guest_id, rows]) => {
    const owed = sumDecimal(rows.map((a) => a.total_price));
    const paid = sumDecimal(rows.map((a) => {
      if (a.payment_amount != null && isNonZero(a.payment_amount)) return a.payment_amount;
      if (a.status === 'paid' || a.status === 'delivered' || a.status === 'collected') return a.total_price;
      return '0';
    }));
    const totalOwed = compareDecimal(owed, paid) === 1 ? (subtractDecimal(owed, paid) ?? '0') : '0';
    const latest = rows.reduce((max, a) => (a.updated_at > max ? a.updated_at : max), rows[0].updated_at);
    return {
      guest_id,
      assignments: rows,
      total_paid: paid,
      total_owed: totalOwed,
      payment_status: !isNonZero(paid) ? 'unpaid' : compareDecimal(paid, owed) !== -1 ? 'fully-paid' : 'partially-paid',
      delivery_address: '',
      created_at: rows[0].created_at,
      updated_at: latest,
    };
  });
}

/**
 * Category/search filtering only — `archived` is a server-side list (fetch
 * with `?archived=true`), not a client filter, so it is not applied here;
 * callers pass whichever list (active or archived) they already loaded.
 */
export function filterFabrics(filter: AsoebiFilter, rows: Fabric[] = fabricsCache): Fabric[] {
  if (filter.category) rows = rows.filter((f) => f.category === filter.category);
  if (filter.searchTerm) {
    const q = filter.searchTerm.toLowerCase();
    rows = rows.filter((f) => f.name.toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q));
  }
  return rows;
}

// ── Orders ("assignments") ──

export const loadAssignments = (): FabricAssignment[] => assignmentsCache;

export const refreshAssignmentsFromApi = async (archived = false): Promise<FabricAssignment[]> => {
  const res = await apiGet<{ assignments: FabricAssignment[] }>(planningPath(`/asoebi/orders${archived ? '?archived=true' : ''}`));
  if (!archived) {
    assignmentsCache = res.assignments;
    dispatchAssignmentsChange();
  }
  return res.assignments;
};

export const createFabricAssignment = async (data: FabricAssignmentCreate): Promise<FabricAssignment> => {
  const created = await apiPost<FabricAssignment>(planningPath('/asoebi/orders'), data);
  assignmentsCache = [...assignmentsCache, created];
  dispatchAssignmentsChange();
  return created;
};

export const updateFabricAssignment = async (id: string, updates: FabricAssignmentUpdate): Promise<FabricAssignment> => {
  const updated = await apiPut<FabricAssignment>(planningPath(`/asoebi/orders/${id}`), updates);
  assignmentsCache = assignmentsCache.map((a) => (a.id === id ? updated : a));
  dispatchAssignmentsChange();
  return updated;
};

export const archiveFabricAssignment = async (id: string): Promise<void> => {
  await apiDelete(planningPath(`/asoebi/orders/${id}`));
  assignmentsCache = assignmentsCache.filter((a) => a.id !== id);
  dispatchAssignmentsChange();
};
export const deleteFabricAssignment = archiveFabricAssignment;

export const restoreFabricAssignment = (id: string): Promise<FabricAssignment> => apiPost<FabricAssignment>(planningPath(`/asoebi/orders/${id}/restore`), {});

export const permanentlyDeleteFabricAssignment = (id: string): Promise<void> => apiDelete(planningPath(`/asoebi/orders/${id}/permanent`));

export const recordPayment = (id: string, payment: PaymentInput): Promise<FabricAssignment> =>
  apiPatch<FabricAssignment>(planningPath(`/asoebi/orders/${id}/payment`), payment);

export const recordBatchPayment = (batch: BatchPaymentInput): Promise<{ assignments: FabricAssignment[] }> =>
  apiPost(planningPath('/asoebi/orders/payment-batch'), batch);

export const listOrderPayments = (id: string) => apiGet<{ payments: unknown[] }>(planningPath(`/asoebi/orders/${id}/payments`));

// ── Overview / revenue / balance / guest picker ──

export const refreshAsoebiOverviewFromApi = async (): Promise<AsoebiOverview> => {
  overviewCache = await apiGet<AsoebiOverview>(planningPath('/asoebi/overview'));
  return overviewCache;
};

export const refreshAsoebiRevenueFromApi = async (): Promise<{ total: string | null; currency: string | null }> => {
  revenueCache = await apiGet<{ total: string | null; currency: string | null }>(planningPath('/asoebi/revenue-total'));
  return revenueCache;
};

export const getAsoebiGuestBalance = (guestId: string) => apiGet<{ owed: string; paid: string; currency: string | null }>(planningPath(`/asoebi/guests/${guestId}/balance`));

export const fetchGuestOptions = (search?: string): Promise<GuestOption[]> =>
  apiGet<{ guests: GuestOption[] }>(planningPath(`/asoebi/guest-options${search ? `?search=${encodeURIComponent(search)}` : ''}`)).then((r) => r.guests);

/**
 * The cards' figures from whatever is currently cached — call AFTER the
 * refreshes above, never instead of them. Revenue is null (shown as a dash,
 * never 0) until refreshAsoebiRevenueFromApi has run and the role can see it.
 */
export function getAsoebiStats(): AsoebiStats {
  const activeFabrics = fabricsCache.filter((f) => !f.archived).length;
  const pendingDeliveries = overviewCache?.pending_deliveries ?? 0;
  return {
    totalFabrics: fabricsCache.length,
    activeFabrics,
    totalAssignments: overviewCache?.assignments_count ?? assignmentsCache.length,
    totalRevenue: revenueCache?.total ?? null,
    pendingDeliveries,
    paidAssignments: assignmentsCache.filter((a) => a.status === 'paid' || a.status === 'delivered' || a.status === 'collected').length,
    pendingConfirmations: assignmentsCache.filter((a) => a.status === 'pending_confirmation').length,
    currency: overviewCache?.currency ?? revenueCache?.currency ?? null,
  };
}
